import type { ReactNode } from 'react'
import { TIER_LABELS } from '../../core/index.js'
import type { BridgeCallout } from '../types.js'
import { HELD_BACK_REASON, STATUS_CLASS, STATUS_LABEL, describeCalloutShift, describeHours, describeOutcome, formatInstant } from './bridgeCalloutText.js'

interface BridgeCalloutListProps {
  callouts: readonly BridgeCallout[]
  empty: string
  /** Something to show on an open callout's row, such as a Cancel button. */
  actions?: (callout: BridgeCallout) => ReactNode
}

/** One callout in full: the shift, who was told and who was held back, every answer so far, and how it ended. */
export function BridgeCalloutRow({ callout, actions }: { callout: BridgeCallout; actions?: ReactNode }) {
  const accepted = callout.filledBy?.workerId ?? null
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${STATUS_CLASS[callout.status]}`}>{STATUS_LABEL[callout.status]}</span>
            {callout.insideLock && (
              <span className="rounded border border-coral/50 bg-coral-lt px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-coral" title="Opened in the last hours before the shift (the facility's lock hours), when only the administrator may act">
                Close to the start
              </span>
            )}
            <span className="text-sm font-bold text-ink">{describeCalloutShift(callout)}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-ink-mid">
            Opened {formatInstant(callout.openedAt)}
            {callout.reason && <> · {callout.reason}</>}
            {callout.pay && (
              <>
                {' '}
                · {callout.pay.currency} {callout.pay.rate.toFixed(2)}/hr{callout.pay.premiumPerHour ? ` + ${callout.pay.premiumPerHour.toFixed(2)} premium` : ''}
              </>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-ink">{describeOutcome(callout)}</div>
        </div>
        {actions}
      </div>
      <div className="grid gap-3 text-[11px] sm:grid-cols-2">
        <div>
          <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Told ({callout.pool.length})</div>
          {callout.pool.length === 0 ? (
            <p className="text-ink-mid">Nobody was enrolled for this day, so nobody was told.</p>
          ) : (
            <ul className="space-y-0.5">
              {callout.pool.map((member) => {
                const answer = callout.responses.find((response) => response.workerId === member.workerId)
                return (
                  <li key={member.workerId} className={member.workerId === accepted ? 'font-bold text-green' : 'text-ink'}>
                    {member.name} <span className="text-ink-mid">· {TIER_LABELS[member.tier]} · {describeHours(member.hoursThisWeek)}</span>
                    {answer && <span className="text-ink-mid"> · {answer.answer === 'accept' ? 'accepted' : 'declined'} {formatInstant(answer.at)}</span>}
                    {member.notifiedAt === null && <span className="text-coral"> · could not be told</span>}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <div>
          <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Held back ({callout.heldBack.length})</div>
          {callout.heldBack.length === 0 ? (
            <p className="text-ink-mid">No one.</p>
          ) : (
            <ul className="space-y-0.5">
              {callout.heldBack.map((held) => (
                <li key={held.workerId} className="text-ink">
                  {held.name} <span className="text-ink-mid">· {HELD_BACK_REASON[held.reason]} · {describeHours(held.hoursThisWeek)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  )
}

/** A facility's callouts as a read-only list, newest first: the dashboard card, the admin's facility dialog and the Partner Simulator all show this. */
export function BridgeCalloutList({ callouts, empty, actions }: BridgeCalloutListProps) {
  if (callouts.length === 0) {
    return <p className="text-xs text-ink-mid">{empty}</p>
  }
  return (
    <ul className="divide-y divide-marine/10 rounded-lg border border-marine/15 bg-white">
      {callouts.map((callout) => (
        <BridgeCalloutRow key={callout.calloutId} callout={callout} actions={actions?.(callout)} />
      ))}
    </ul>
  )
}
